// ============================================================
// EmbeddedSystems.jsx
//
// Course content page: CONCEPTS lesson — "what is an embedded
// system and how is it built." This is the theory half of the
// Embedded Systems pair; EmbeddedSystemsTutorials.jsx is the
// hands-on, step-by-step companion with its own distinct worked
// example (an interrupt-driven lab, not a repeat of this page's
// illustrative snippet) — the two previously overlapped almost
// completely (same architecture cards, same register-blink
// snippet, same Arduino/STM32 badges); they're now differentiated
// by role, and each links to the other.
//
// Fixes applied:
//   1) the "Direct Register Manipulation" C example had no
//      #include directives and no main()/entry point at all — just
//      a bare statement followed by an unreachable while(1) loop.
//      Without avr/io.h + util/delay.h and a main(), this doesn't
//      compile. Wrapped it in a real `int main(void)` with the
//      needed includes.
//   2) the copy button fired `navigator.clipboard.writeText()`
//      without awaiting it or catching a failure. Wrapped in
//      async/await + try/catch.
//   3) removed a dead MUI icon import (MemoryIcon was imported but
//      never rendered) — now used deliberately for the Volatile
//      Memory card.
//   4) re-themed from this lesson's one-off sky-blue/copper palette
//      to the app-wide dark-purple/goldenrod look, moved to
//      arrow-function components, and replaced the Arduino/STM32
//      brand-logo icons with semantic Material Icons (the hardware
//      family comparison now lives in the Tutorials lesson instead,
//      so it isn't duplicated here).
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import MemoryIcon from "@mui/icons-material/Memory";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import CodeIcon from "@mui/icons-material/Code";
import ConstructionIcon from "@mui/icons-material/Construction";
import TerminalIcon from "@mui/icons-material/Terminal";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const ARCHITECTURE = [
    { title: "Processing Core", icon: <DeveloperBoardIcon />, value: "ARM / AVR", details: "The CPU executes instructions from flash memory, handling logic and arithmetic." },
    { title: "Volatile Memory", icon: <MemoryIcon />, value: "SRAM", details: "Fast data storage for runtime variables. Lost when power is removed." },
    { title: "Peripherals", icon: <ElectricalServicesIcon />, value: "I/O Ports", details: "GPIO, UART, ADC, and PWM — how the chip interacts with the physical world." },
];

const APPLICATIONS = [
    { title: "Automotive", desc: "ECUs for engine timing and ABS." },
    { title: "Consumer", desc: "Wearables and smart appliances." },
    { title: "Medical", desc: "Pacemakers and diagnostic tools." },
    { title: "Robotics", desc: "Motor controllers and sensor fusion." },
];

// A complete, compiling example — the original had no #include
// directives and no main()/entry point, just a bare statement
// followed by an unreachable loop.
const REGISTER_SNIPPET = `#include <avr/io.h>
#include <util/delay.h>

int main(void) {
  // Set Pin 13 (PB5 on the ATmega328P) as an output
  DDRB |= (1 << DDB5);

  while (1) {
    PORTB |= (1 << PORTB5);  // SET HIGH
    _delay_ms(500);
    PORTB &= ~(1 << PORTB5); // SET LOW
    _delay_ms(500);
  }
}`;

// Sub-components (arrow-function style, defined before first use)

const SectionHeader = ({ icon, label }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                background: "rgba(218,165,32,0.1)",
                border: `1px solid ${BRAND.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {React.cloneElement(icon, { style: { color: BRAND.gold, fontSize: 20 } })}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: BRAND.white, letterSpacing: "-0.5px" }}>{label}</Typography>
    </Box>
);

const ArchitectureCard = ({ title, icon, value, details }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 3,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: BRAND.teal, transform: "scale(1.02)" },
        }}
    >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            {React.cloneElement(icon, { style: { color: BRAND.teal, fontSize: 24 } })}
            <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
        </Stack>
        <Typography variant="h4" sx={{ color: BRAND.gold, fontWeight: 900, mb: 1 }}>{value}</Typography>
        <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{details}</Typography>
    </Paper>
);

const CodeSnippet = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 2, border: `1px solid ${BRAND.border}`, mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.muted }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Typography component="pre" sx={{ p: 2, color: "#FCD34D", fontSize: 13, fontFamily: "'Fira Code', monospace", overflowX: "auto" }}>
                {code}
            </Typography>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const EmbeddedSystems = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 8 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Embedded Engineering · Module 02a: Concepts"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-2px" }}>
                    Embedded Systems & <span style={{ color: BRAND.teal }}>Microcontrollers</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 700, lineHeight: 1.6 }}>
                    Embedded systems are the "invisible brains" inside modern hardware. This lesson covers the
                    concepts — architecture, memory, and peripherals — before you touch a breadboard in the
                    hands-on Tutorials lesson that follows.
                </Typography>
            </Box>

            {/* ARCHITECTURE SECTION */}
            <Box sx={{ mb: 8 }}>
                <SectionHeader icon={<AccountTreeIcon />} label="Hardware Architecture" />
                <Grid container spacing={3}>
                    {ARCHITECTURE.map((item) => (
                        <Grid item xs={12} md={4} key={item.title}>
                            <ArchitectureCard {...item} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ borderColor: BRAND.border, mb: 8 }} />

            {/* PROGRAMMING SECTION */}
            <Grid container spacing={6} sx={{ mb: 8 }}>
                <Grid item xs={12} md={6}>
                    <SectionHeader icon={<CodeIcon />} label="Low-Level Control" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, lineHeight: 1.8 }}>
                        Unlike PC development, embedded programming requires direct interaction with{" "}
                        <strong>hardware registers</strong>. You'll learn to manipulate bits to control
                        peripherals with microsecond precision.
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                        Ready to try this on real hardware? The <strong>Embedded Systems Tutorials</strong>{" "}
                        lesson picks up from here with a step-by-step interrupt-driven lab, a parts list, and
                        its own worked example.
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <CodeSnippet label="blink_register.c (Direct Register Manipulation)" code={REGISTER_SNIPPET} />
                </Grid>
            </Grid>

            {/* APPLICATIONS GRID */}
            <Box sx={{ mb: 8 }}>
                <SectionHeader icon={<ConstructionIcon />} label="Real-World Impact" />
                <Grid container spacing={2}>
                    {APPLICATIONS.map((item) => (
                        <Grid item xs={6} md={3} key={item.title}>
                            <Box sx={{ p: 3, textAlign: "center", borderRadius: 4, background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" sx={{ color: BRAND.teal, mb: 1 }}>{item.title}</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>{item.desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 6, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Interface?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                    Continue to Embedded Systems Tutorials to wire up real hardware and write your first
                    interrupt-driven firmware.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Go to Tutorials Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 2 }} />
                    <Chip label="View Datasheets" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.teal, color: BRAND.teal }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default EmbeddedSystems;
